import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import messages from '../messages';
import { useTheme } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';
import { getStyles } from './styles';
import GeneralTable from '../../../../components/GeneralTable';
import { ReactSVG } from 'react-svg';
import { IconSvg } from '../../../../utils/globalIcons';
import { tostAlert } from '../../../../utils/AlertToast';
import LoadBar from '../../../../utils/LoadBar';
import MonitoringServiceApi from '../../../../Api/Services/AmeyaBackend/MonitoringServiceApi';
import { apiErrorHandler } from '../../../../utils/ApiErrorHandler';
import ExtractionTriggerApi from '../../../../Api/Services/AppflyteBackend/ExtractionTriggerApi';
import { setExtractionIntegrationAdded, setExtractionTriggerAdded } from '../../../../Redux/slice/newDataSlice';
import { useDispatch } from 'react-redux';

function ExtractionTriggers({ dataLoading, triggers }) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const intl = useIntl();

    const [loading, setLoading] = useState(false);

    const handleTriggerDelete = async (item) => {
        try {

            const trigger_id = item?.payload?.__auto_id__;
            const trigger_update_key = item?.update_key;
            const trigger_name = item?.payload?.name;
            const trigger_type = item?.payload?.type;
            const job_id = item?.payload?.job_id;

            const confirm = window.confirm(`${intl.formatMessage(messages.trigger_delete_check)}: ${trigger_name}?`);
            if (!confirm) {
                return
            }

            setLoading(true)

            const data_keys = ["google_drive", "one_drive", "gmail", "outlook"]
            if (data_keys?.includes(trigger_type) || false) {
                try {
                    await MonitoringServiceApi.deleteJobs(JSON.stringify({ "job_ids": job_id }));
                } catch (error) {
                    console.log(error)
                    // const error_response = error?.response?.data?.detail ?? null;
                    // if (error_response !== `Job ${trigger_id} not found`) {
                    //     tostAlert(intl.formatMessage(messages.trigger_delete_error), 'error');
                    //     return
                    // }
                }
            }

            const reqObj = { "id": trigger_id, "fields": [{ "path": "$.extraction_integrations", "value": [] }] }
            const update_response = await ExtractionTriggerApi.update(JSON.stringify(reqObj), trigger_id, trigger_update_key);
            if (update_response.status !== 200) {
                tostAlert(intl.formatMessage(messages.trigger_delete_error), 'error');
                return
            }

            const response = await ExtractionTriggerApi.delete(trigger_id);
            if (response.status === 200) {
                tostAlert(intl.formatMessage(messages.trigger_delete_success), 'success');
                dispatch(setExtractionTriggerAdded(true))
                dispatch(setExtractionIntegrationAdded(true))
            } else {
                tostAlert(intl.formatMessage(messages.trigger_delete_error), 'error');
            }

        } catch (err) {
            console.error(err);
            apiErrorHandler(err)
        } finally {
            setLoading(false)
        }
    };

    const handleTriggerEdit = async (item) => {
        try {
            navigate('/connectors/extraction-agent/triggers/add', {
                state: {
                    trigger_data: item,
                    action_type: 'put'
                }
            })
        } catch (error) {
            console.log(error)
        }
    }

    const connectorNames = {
        google_drive: "Google Drive",
        one_drive: "One Drive",
        aws_s3: "AWS S3",
        webhook: "Webhook",
        azure_blob: "Azure Blob",
        gmail: "Gmail",
        outlook: "Outlook"
    };

    const columns = [
        {
            id: 'payload.name',
            label: 'Name',
            sortable: true,
            renderCell: (row) => row?.payload?.name
        },
        {
            id: 'payload.type',
            label: 'Type',
            sortable: true,
            renderCell: (row) => connectorNames[row?.payload?.type] || row?.payload?.type
        },
        {
            id: 'payload.monitoring_status',
            label: 'Status',
            sortable: true,
            renderCell: (row) =>
                <Box display={'flex'} alignItems={'center'}>
                    <span style={{ marginRight: '10px' }}>
                        <ReactSVG
                            src={row?.payload?.error ? IconSvg.errorIcon : IconSvg.trainedIcon}
                            beforeInjection={(svg) => {
                                svg.setAttribute('style', 'width:24px; height:24px; display:block;');
                            }}
                        />
                    </span>
                    <span>{row?.payload?.error ? "Error" : "Active"}</span>
                </Box>
        },
        {
            id: 'payload.type',
            label: 'Integration',
            sortable: true,
            renderCell: (row) =>
                <Box display={'flex'} alignItems={'center'}>
                    <span style={{ marginRight: '10px' }}>
                        <ReactSVG
                            src={{
                                google_drive: IconSvg.gDriveIcon,
                                one_drive: IconSvg.onedriveIcon,
                                gmail: IconSvg.mailIcon,
                                outlook: IconSvg.mailIcon,
                                webhook: IconSvg.apiIcon,
                                aws_s3: IconSvg.awsIcon,
                                azure_blob: IconSvg.azureBlobIcon
                            }[row?.payload?.type]}
                            beforeInjection={(svg) => {
                                svg.setAttribute('style', 'width:24px; height:24px; display:block;');
                                svg.setAttribute('stroke', '#0B51C5');
                            }}
                        />
                    </span>
                    <span>{connectorNames[row?.payload?.connector_name] || row?.payload?.connector_name}</span>
                </Box>
        },
        {
            id: '',
            label: '',
            sortable: false,
            renderCell: (row) =>
                <Box display={'flex'} alignItems={'center'}>
                    <span
                        style={{ marginRight: '10px' }}
                        onClick={() => handleTriggerEdit(row)}
                    >
                        <ReactSVG
                            src={IconSvg.editIcon}
                            beforeInjection={(svg) => {
                                svg.setAttribute('style', 'width:20px; height:20px; display:block;');
                                svg.setAttribute('stroke', '#0B51C5');
                            }}
                        />
                    </span>
                    <span
                        onClick={() => handleTriggerDelete(row)}
                    >
                        <ReactSVG
                            src={IconSvg.deleteIcon}
                            beforeInjection={(svg) => {
                                svg.setAttribute('style', 'width:20px; height:20px; display:block;');
                                svg.setAttribute('stroke', '#C30E2E');
                            }}
                        />
                    </span>
                </Box>
        }
    ];

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.cardContainer}>

                {(loading || dataLoading) && <LoadBar />}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                    <Typography sx={styles.mainHeadingText}>Triggers</Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button sx={{ ...styles.saveBtn, width: '135px' }}
                            onClick={() => navigate('/connectors/extraction-agent/triggers/add', {
                                state: {
                                    trigger_data: {},
                                    action_type: 'post'
                                }
                            })}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>
                                ADD TRIGGER
                            </Typography>
                        </Button>
                    </Box>
                </Box>

                <Box marginTop={'30px'} paddingBottom={'20px'}>
                    <GeneralTable
                        data={triggers}
                        columns={columns}
                        loading={loading || dataLoading}
                    />
                </Box>
            </Box>
        </Box>
    )
}

export default ExtractionTriggers;